import { useState } from 'react';
import { GatePrompt } from './account-gate';

/**
 * Save/unsave an event. Signed-out visitors get a gentle sign-in prompt (the
 * carrot that captures the account); members get an optimistic toggle.
 */
export function SaveButton({
  eventId,
  initialSaved,
  loggedIn,
  next,
}: {
  eventId: string;
  initialSaved: boolean;
  loggedIn: boolean;
  next: string;
}) {
  const [saved, setSaved] = useState(initialSaved);
  const [pending, setPending] = useState(false);

  if (!loggedIn) {
    return <GatePrompt next={next}>♡ Save this event — free with an account</GatePrompt>;
  }

  async function toggle() {
    setPending(true);
    const wasSaved = saved;
    setSaved(!wasSaved); // optimistic
    try {
      if (wasSaved) {
        await fetch(`/api/me/saves/${eventId}`, { method: 'DELETE', credentials: 'include' });
      } else {
        await fetch('/api/me/saves', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ eventId }),
        });
      }
    } catch {
      setSaved(wasSaved); // revert on network failure
    } finally {
      setPending(false);
    }
  }

  return (
    <button
      type="button"
      className={`ui-btn btn-sm save-btn ${saved ? 'is-saved' : 'btn-secondary'}`}
      onClick={toggle}
      disabled={pending}
      aria-pressed={saved}
    >
      {saved ? '♥ Saved — we’ll remind you' : '♡ Save event'}
    </button>
  );
}
